import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export const EASE = [0.22, 1, 0.36, 1];

export const slugify = (s) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/-{2,}/g, "-").slice(0, 60);

export const scrollToId = (id) => {
    const el = document.querySelector(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
    else el.scrollIntoView({ behavior: "smooth" });
};

export const Reveal = ({ children, delay = 0, className = "", y = 36 }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay, ease: EASE }}
    >
        {children}
    </motion.div>
);

export const Eyebrow = () => null;

export const PurpleMark = ({ className = "h-6 w-6" }) => (
    <svg viewBox="0 0 64 64" fill="none" className={className} aria-hidden="true">
        <path d="M12 14 H52 L32 50 Z" stroke="currentColor" strokeWidth="5" strokeLinejoin="round" />
        <circle cx="12" cy="14" r="5" fill="currentColor" />
        <circle cx="52" cy="14" r="5" fill="currentColor" />
        <circle cx="32" cy="50" r="5" fill="currentColor" />
    </svg>
);

export const BlackCTA = ({ children, target = "#become", testId, className = "" }) => (
    <button
        data-testid={testId}
        onClick={() => target && scrollToId(target)}
        className={`group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-base font-bold text-white transition-colors duration-300 hover:bg-neutral-700 ${className}`}
    >
        {children}
        <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">→</span>
    </button>
);

export const Section = ({ id, children, className = "" }) => (
    <section id={id} className="px-3 py-1.5 sm:px-5 sm:py-2.5">
        <div className={cn("mx-auto max-w-[1320px] rounded-[2rem] bg-white px-5 py-16 sm:rounded-[3rem] sm:px-12 sm:py-24", className)}>
            <div className="mx-auto max-w-6xl">{children}</div>
        </div>
    </section>
);

export const LazyVideo = ({ src, className = "" }) => {
    const ref = useRef(null);
    useEffect(() => {
        const v = ref.current;
        if (!v) return;
        const io = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) v.play().catch(() => {});
                else v.pause();
            },
            { threshold: 0.15 }
        );
        io.observe(v);
        return () => io.disconnect();
    }, []);
    return (
        <video ref={ref} loop muted playsInline preload="metadata" className={className}>
            <source src={src.replace(".mp4", ".webm")} type="video/webm" />
            <source src={src} type="video/mp4" />
        </video>
    );
};
